import { PauseIcon, PlayIcon } from '@heroicons/react/outline';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Voice } from 'types';

import clsxm from '@/lib/clsxm';

import { getVoiceImageUrl } from '@/utils/voices';

import {
  SELECTED_VOICE_STORAGE_KEY,
  TTS_LANDING_CONTEXT_KEY,
} from './VoicesForLandingPage';

export const TTS_LANDING_TEXT_KEY = 'ttsLandingText';

interface TTSLandingFormProps {
  voices: Voice[];
  languageName: string;
  isAccent?: boolean;
  isStyle?: boolean;
}

const TTSLandingForm: React.FC<TTSLandingFormProps> = ({
  voices,
  languageName,
  isAccent = false,
  isStyle = false,
}) => {
  const { t } = useLocale('text-to-speech');
  const [text, setText] = useState('');
  const [selectedVoiceIndex, setSelectedVoiceIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const displayVoices = useMemo(() => {
    const seenVoiceIds = new Set<string>();

    return voices
      .filter((voice) => {
        if (seenVoiceIds.has(voice.voice_id)) {
          return false;
        }

        seenVoiceIds.add(voice.voice_id);
        return true;
      })
      .slice(0, 6);
  }, [voices]);
  const safeSelectedVoiceIndex = Math.min(
    selectedVoiceIndex,
    Math.max(displayVoices.length - 1, 0),
  );
  const currentVoice = displayVoices[safeSelectedVoiceIndex];

  // Stable random heights for sound bar animation
  const barHeights = useMemo(
    () => [1, 2, 3, 4].map(() => 8 + Math.random() * 12),
    [],
  );

  // Pause audio on unmount
  useEffect(() => {
    const audio = audioRef.current;

    return () => {
      audio?.pause();
    };
  }, []);

  const playSample = () => {
    if (!currentVoice?.preview_url || !audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.src = currentVoice.preview_url;
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
  };

  const handleTryNow = () => {
    // Style pages don't map to a language/accent filter on the TTS page —
    // the selected voice itself carries over via SELECTED_VOICE_STORAGE_KEY.
    // Clear any context a previously visited language page left behind so
    // /tts doesn't apply a stale filter.
    if (isStyle) {
      localStorage.removeItem(TTS_LANDING_CONTEXT_KEY);
    } else {
      const context = isAccent
        ? { accent: languageName }
        : { language: languageName };
      localStorage.setItem(TTS_LANDING_CONTEXT_KEY, JSON.stringify(context));
    }
    if (currentVoice) {
      localStorage.setItem(
        SELECTED_VOICE_STORAGE_KEY,
        JSON.stringify({
          name: currentVoice.name,
          voice_id: currentVoice.voice_id,
        }),
      );
    }
    if (text.trim()) {
      localStorage.setItem(TTS_LANDING_TEXT_KEY, JSON.stringify(text));
    }
    window.location.href = '/tts';
  };

  return (
    <div className='relative mx-auto w-full lg:max-w-xl'>
      <div className='overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-2xl shadow-primary-500/10 dark:border-slate-700/50 dark:bg-slate-800'>
        {/* Header */}
        <div className='border-b border-gray-100 bg-gradient-to-r from-primary-50 to-purple-50 px-5 py-3 dark:border-slate-700/50 dark:from-primary-900/20 dark:to-purple-900/20'>
          <div className='flex items-center gap-2'>
            <div className='flex gap-1.5'>
              <div className='h-3 w-3 rounded-full bg-red-400' />
              <div className='h-3 w-3 rounded-full bg-yellow-400' />
              <div className='h-3 w-3 rounded-full bg-green-400' />
            </div>
            <span className='ml-2 text-xs font-medium text-gray-500 dark:text-slate-400'>
              {t('Text to Speech')}
            </span>
          </div>
        </div>

        <div className='p-5'>
          {/* Text Input */}
          <div className='relative'>
            <textarea
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 1000))}
              placeholder={t('Type something to hear it spoken aloud...')}
              className='block w-full resize-none rounded-xl border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-primary-400 focus:bg-white focus:ring-2 focus:ring-primary-500/20 dark:border-slate-600 dark:bg-slate-700/50 dark:text-white dark:placeholder-slate-500 dark:focus:border-primary-500 dark:focus:bg-slate-700'
            />
            <span className='absolute bottom-2 right-3 text-[10px] text-gray-300 dark:text-slate-600'>
              {text.length}/1000
            </span>
          </div>

          {/* Voice Picker */}
          <div className='mt-4'>
            <div className='mb-2 flex items-center justify-between'>
              <p className='text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-slate-500'>
                {t('Choose a voice')}
              </p>
              <a
                href='#voices-section'
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById('voices-section')
                    ?.scrollIntoView({ behavior: 'smooth' });
                }}
                className='text-xs font-medium text-primary-500 transition-colors hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300'
              >
                {t('See all voices')} &darr;
              </a>
            </div>
            <div className='grid grid-cols-3 gap-2 sm:grid-cols-6'>
              {displayVoices.map((voice, idx) => (
                <button
                  key={voice.voice_id}
                  type='button'
                  onClick={() => {
                    if (idx !== selectedVoiceIndex && audioRef.current) {
                      audioRef.current.pause();
                      audioRef.current.currentTime = 0;
                      setIsPlaying(false);
                    }
                    setSelectedVoiceIndex(idx);
                  }}
                  className={clsxm(
                    'group relative flex flex-col items-center rounded-xl p-2 transition-all',
                    safeSelectedVoiceIndex === idx
                      ? 'bg-primary-50 ring-2 ring-primary-500 dark:bg-primary-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-slate-700/50',
                  )}
                >
                  <div className='relative'>
                    <Image
                      alt={voice.name}
                      src={getVoiceImageUrl(voice.image)}
                      width='40'
                      height='40'
                      className='rounded-full'
                      loading='lazy'
                    />
                    {safeSelectedVoiceIndex === idx && (
                      <div className='absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-primary-500 dark:border-slate-800' />
                    )}
                  </div>
                  <span
                    className={clsxm(
                      'mt-1 truncate text-center text-[10px] font-medium leading-tight',
                      safeSelectedVoiceIndex === idx
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-gray-500 dark:text-slate-400',
                    )}
                    style={{ maxWidth: '60px' }}
                  >
                    {voice.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Selected Voice Info + Play */}
          {currentVoice && (
            <div className='mt-4 flex items-center gap-3 rounded-xl bg-gray-50 p-3 dark:bg-slate-700/30'>
              <button
                type='button'
                onClick={playSample}
                disabled={!currentVoice.preview_url}
                className={clsxm(
                  'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-all',
                  currentVoice.preview_url
                    ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30 hover:bg-primary-600 active:scale-95'
                    : 'cursor-not-allowed bg-gray-200 text-gray-400 dark:bg-slate-600 dark:text-slate-500',
                )}
              >
                {isPlaying ? (
                  <PauseIcon className='h-5 w-5' />
                ) : (
                  <PlayIcon className='h-5 w-5' />
                )}
              </button>
              <div className='min-w-0 flex-1'>
                <p className='truncate text-sm font-semibold text-gray-900 dark:text-white'>
                  {currentVoice.name}
                </p>
                <p className='truncate text-xs text-gray-500 dark:text-slate-400'>
                  {currentVoice.accent &&
                  currentVoice.accent !==
                    (Array.isArray(currentVoice.language)
                      ? currentVoice.language[0]
                      : currentVoice.language)
                    ? `${t(Array.isArray(currentVoice.language) ? currentVoice.language[0] : currentVoice.language || '')} · ${t(currentVoice.accent)}`
                    : t(
                        Array.isArray(currentVoice.language)
                          ? currentVoice.language[0]
                          : currentVoice.language || '',
                      )}
                </p>
              </div>
              {isPlaying && (
                <div className='flex items-center gap-0.5'>
                  {barHeights.map((h, i) => (
                    <div
                      key={i}
                      className='w-0.5 animate-pulse rounded-full bg-primary-500'
                      style={{
                        height: `${h}px`,
                        animationDelay: `${(i + 1) * 0.15}s`,
                        animationDuration: '0.6s',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CTA Button */}
          <button
            type='button'
            onClick={handleTryNow}
            className='mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/25 transition-all hover:from-primary-700 hover:to-purple-700 hover:shadow-xl hover:shadow-primary-500/30 active:scale-[0.98]'
          >
            {t('Try it Free')}
            <svg className='h-4 w-4' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z'
                clipRule='evenodd'
              />
            </svg>
          </button>

          <p className='mt-2 text-center text-[11px] text-gray-400 dark:text-slate-500'>
            {t('No credit card required')} · {t('1,900+ voices available')}
          </p>
        </div>
      </div>
      <audio ref={audioRef} onEnded={handleAudioEnd} />
    </div>
  );
};

export default TTSLandingForm;
